import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Button from "../../components/ui/Button/Button.jsx";
import Banner from "../../components/ui/Banner/Banner.jsx";
import FormField from "../../components/forms/FormField/FormField.jsx";
import TextInput from "../../components/forms/TextInput/TextInput.jsx";
import { money } from "../../utils/format.js";
import { getPurchaseConfig, createPurchaseIntent, getPurchaseStatus } from "../../services/subscriptionService.js";

// Same pattern as PaymentDetailsStep's FeeForm: collect name/ZIP with our own
// fields and pass a fixed billing country via payment_method_data at confirm
// time, instead of letting the Payment Element ask for a full billing address
// (which shows a "Country" dropdown filers here don't need to see).
const paymentElementOptions = {
  fields: { billingDetails: { address: "never" } },
};

const BILLING_COUNTRY = "US";

function friendlyStripeError(error) {
  if (!error) return "We couldn't process your payment. Please try again.";
  if (error.type === "card_error" || error.type === "validation_error") {
    return error.message || "Your card could not be processed. Please check the details and try again.";
  }
  return "We couldn't process your payment. Please try again.";
}

// Inner form - lives inside <Elements> so it can use the Stripe hooks.
function CheckoutForm({ pkg, amountCents, showToast, onClose, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(""); // '' | 'finalizing'
  const [form, setForm] = useState({ firstName: "", lastName: "", zip: "" });
  const [errors, setErrors] = useState({});

  // Same input-scrubbing as PaymentDetailsStep's FeeForm.
  const handleNameChange = (key) => (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z\-' ]/g, "")
      .replace(/ {2,}/g, " ")
      .replace(/-{2,}/g, "-")
      .replace(/'{2,}/g, "'")
      .slice(0, 20);
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleZipChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 12);
    const val = raw.length <= 5 ? raw : `${raw.slice(0, 5)}-${raw.slice(5)}`;
    setForm((prev) => ({ ...prev, zip: val }));
    setErrors((prev) => ({ ...prev, zip: "" }));
  };

  const validate = () => {
    const errs = {};
    const first = form.firstName.trim();
    const last = form.lastName.trim();

    if (!first) errs.firstName = "First name is required.";
    else if (!/^[A-Za-z\-' ]+$/.test(first)) errs.firstName = "First name can only contain letters, hyphens, apostrophes, and spaces.";

    if (!last) errs.lastName = "Last name is required.";
    else if (!/^[A-Za-z\-' ]+$/.test(last)) errs.lastName = "Last name can only contain letters, hyphens, apostrophes, and spaces.";

    if (!/^\d{5}$|^\d{5}-\d{4}$|^\d{5}-\d{7}$/.test(form.zip)) {
      errs.zip = "Invalid ZIP code. Use 5, 9 (XXXXX-XXXX), or 12 (XXXXX-XXXXXXX) digits.";
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      showToast(Object.values(errs)[0]);
      return false;
    }
    return true;
  };

  // Same poll-until-final pattern as PaymentDetailsStep's FeeForm.waitForFinal.
  const waitForFinal = async (userSubscriptionId) => {
    for (let i = 0; i < 8; i++) {
      try {
        const s = await getPurchaseStatus(userSubscriptionId);
        if (s?.paymentStatus === "SUCCESS") return "SUCCESS";
        if (s?.paymentStatus === "FAILED") return "FAILED";
      } catch {
        /* keep polling */
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    return "PENDING";
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!stripe || !elements) {
      showToast("Payment form is still loading. Please try again in a moment.");
      return;
    }
    setIsSubmitting(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showToast(friendlyStripeError(submitError));
        return;
      }

      let clientSecret;
      let userSubscriptionId;
      try {
        const created = await createPurchaseIntent(pkg.packageId);
        clientSecret = created.clientSecret;
        userSubscriptionId = created.userSubscriptionId;
      } catch (err) {
        showToast(err.message || "We couldn't start your purchase. Please try again.");
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: `${form.firstName} ${form.lastName}`.trim(),
              address: {
                line1: "",
                city: "",
                state: "",
                postal_code: form.zip,
                country: BILLING_COUNTRY,
              },
            },
          },
        },
      });

      if (error) {
        showToast(friendlyStripeError(error));
        return;
      }

      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        setStatus("finalizing");
        const final = await waitForFinal(userSubscriptionId);
        if (final === "FAILED") {
          showToast("Your payment couldn't be completed. Please try again.");
          return;
        }
        if (final === "PENDING") {
          showToast("Your payment is still processing. We'll confirm it shortly.");
          return;
        }
        showToast("Purchase successful! Your return credits are ready to use.");
        onSuccess(userSubscriptionId);
      } else {
        showToast("We couldn't confirm your payment. Please try again.");
      }
    } catch {
      showToast("We couldn't process your payment. Please try again.");
    } finally {
      setIsSubmitting(false);
      setStatus("");
    }
  };

  return (
    <>
      <div className="p-6">
        <div className="grid gap-3.5 sm:grid-cols-2">
          <FormField label="First name" required error={errors.firstName} showError={!!errors.firstName}>
            <TextInput
              placeholder="John"
              value={form.firstName}
              onChange={handleNameChange("firstName")}
              onBlur={() => setForm((p) => ({ ...p, firstName: p.firstName.replace(/\s+$/, "") }))}
              maxLength={20}
              invalid={!!errors.firstName}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Last name" required error={errors.lastName} showError={!!errors.lastName}>
            <TextInput
              placeholder="Doe"
              value={form.lastName}
              onChange={handleNameChange("lastName")}
              onBlur={() => setForm((p) => ({ ...p, lastName: p.lastName.replace(/\s+$/, "") }))}
              maxLength={20}
              invalid={!!errors.lastName}
              disabled={isSubmitting}
            />
          </FormField>
        </div>

        <FormField label="Card details" required>
          <div className="border border-[var(--line-2)] rounded-[var(--r-sm)] p-3 bg-[var(--panel)]">
            <PaymentElement options={paymentElementOptions} />
          </div>
        </FormField>

        <FormField label="ZIP" required error={errors.zip} showError={!!errors.zip} style={{ maxWidth: 220 }}>
          <TextInput
            mono
            placeholder="10001 or 10001-1234"
            value={form.zip}
            onChange={handleZipChange}
            maxLength={13}
            invalid={!!errors.zip}
            disabled={isSubmitting}
          />
        </FormField>

        <div className="border border-[var(--line-2)] rounded-[var(--r-sm)] p-3 bg-[var(--panel-2)] mt-4">
          <div className="flex justify-between text-[13px] text-[var(--muted)] mb-1">
            <span>Base price</span>
            <span>{money(pkg.baseAmount)}</span>
          </div>
          {Number(pkg.discountAmount) > 0 && (
            <div className="flex justify-between text-[13px] text-[var(--ok)] mb-1">
              <span>Discount</span>
              <span>-{money(pkg.discountAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-[14px] font-semibold text-[var(--ink)] pt-2 mt-1 border-t border-[var(--line-2)]">
            <span>Total</span>
            <span>{money(amountCents / 100)}</span>
          </div>
        </div>
      </div>

      <div className="py-4 px-6 border-t border-[var(--line)] flex justify-between gap-3">
        <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button disabled={isSubmitting || !stripe} onClick={handleSubmit}>
          {status === "finalizing" ? "Finalizing…" : isSubmitting ? "Processing…" : `Pay ${money(amountCents / 100)} →`}
        </Button>
      </div>
    </>
  );
}

// pkg: one package tile from getSubscriptionPackages() ({ packageId, packageQuantity,
// returnsIncluded, baseAmount, discountAmount, finalAmount, ... }), plus categoryName/code
// merged in by the caller for display.
const SubscriptionPurchaseModal = ({ pkg, showToast, onClose, onSuccess }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [amountCents, setAmountCents] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cfg = await getPurchaseConfig(pkg.packageId);
        if (!active) return;
        if (!cfg?.publishableKey) {
          setError("Purchases are temporarily unavailable. Please try again later.");
          return;
        }
        setAmountCents(cfg.amount);
        setStripePromise(loadStripe(cfg.publishableKey));
      } catch (err) {
        if (active) setError(err.message || "Purchases are temporarily unavailable. Please try again later.");
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg.packageId]);

  return (
    <div
      className="fixed inset-0 z-[800] flex items-center justify-center p-5"
      style={{ background: "rgba(15,23,42,.5)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[var(--panel)] rounded-[20px] w-full max-w-[520px] shadow-[0_24px_64px_rgba(0,0,0,0.3)] overflow-hidden border border-[var(--line)]">
        <div className="py-6 px-6" style={{ background: "var(--ink)" }}>
          <div className="text-[1.1rem] font-extrabold text-white">
            {pkg.categoryName} — {pkg.packageQuantity === 1 ? "1 unit" : `${pkg.packageQuantity} units`}
          </div>
          <div className="text-[13px] text-white/50 mt-[3px]">
            File up to {pkg.returnsIncluded} return{pkg.returnsIncluded === 1 ? "" : "s"} — credits never
            expire
          </div>
        </div>

        {error ? (
          <div className="p-6">
            <Banner tone="danger" icon="⚠">
              {error}
            </Banner>
            <Button variant="ghost" block className="mt-4" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : !stripePromise ? (
          <div className="p-8 text-center text-[var(--muted)]">Loading secure payment form…</div>
        ) : (
          <Elements stripe={stripePromise} options={{ mode: "payment", amount: amountCents, currency: "usd", appearance: { theme: "stripe" } }}>
            <CheckoutForm pkg={pkg} amountCents={amountCents} showToast={showToast} onClose={onClose} onSuccess={onSuccess} />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPurchaseModal;
